import { Controller } from 'stimulus'
import { BREAKPOINTS } from '../base/consts'

const MIN_SPEED = 8
const MAX_SPEED = 10

const TIMEOUT_MIN = 10000
const TIMEOUT_MAX = 15000

const ANIMATION_DURATION = 800

export default class Valentine extends Controller {
  static values = {
    numberOfEditors: Number,
  }

  animationFrame

  screenBoundaries = {
    top: 0,
    left: 0,
    right: null,
    bottom: null,
  }

  easings = { linear: Valentine.linear, quad: Valentine.quad, circ: Valentine.circ }

  connect() {
    if (window.innerWidth >= BREAKPOINTS.VIEWPORT_L) {
      this.initScreenBoundaries()
      setTimeout(this.startValentine.bind(this), Valentine.randomIntFromInterval(3000, 8000))
    }
  }

  initScreenBoundaries() {
    this.screenBoundaries.top = window.scrollY
    this.screenBoundaries.right = window.innerWidth
    this.screenBoundaries.bottom = window.scrollY + window.innerHeight

    window.addEventListener('scroll', () => {
      this.screenBoundaries.top = window.scrollY
      this.screenBoundaries.bottom = window.scrollY + window.innerHeight
    })
  }

  startValentine() {
    Valentine.animate({
      screenBoundaries: this.screenBoundaries,
      draw: Valentine.draw,
      duration: ANIMATION_DURATION,
      easing: this.easings.linear,
    })
  }

  static animate({ screenBoundaries, draw, duration, easing }) {
    const item = Valentine.initValentineItem()
    const randomStartingPoint = Valentine.getRandomStartingPointOutsideBoundaries(
      item.getBoundingClientRect(),
      screenBoundaries
    )
    Valentine.setItemPosition(item, randomStartingPoint.x, randomStartingPoint.y)

    const start = performance.now()

    // calculate Direction
    const direction = Valentine.getDirection(item, screenBoundaries)
    Valentine.updateDrawDirection(item, direction)

    // calculate Speed
    const speed = {
      x: Valentine.randomIntFromInterval(MIN_SPEED, MAX_SPEED),
      y: Valentine.randomIntFromInterval(MIN_SPEED, MAX_SPEED),
    }

    requestAnimationFrame(function animate(time) {
      // timeFraction goes from 0 to 1
      let timeFraction = (time - start) / duration
      if (timeFraction > 1) timeFraction = 1

      // calculate the current animation state
      const progress = easing(timeFraction)

      draw(item, direction, speed, progress) // draw it

      if (timeFraction < 1) {
        requestAnimationFrame(animate)
      } else {
        item.classList.add('finished')
        setTimeout(
          () =>
            Valentine.animate({
              screenBoundaries: screenBoundaries,
              draw: Valentine.draw,
              duration: ANIMATION_DURATION,
              easing: easing,
            }),
          Valentine.randomIntFromInterval(TIMEOUT_MIN, TIMEOUT_MAX)
        )
      }
    })
  }

  static updateDrawDirection(item, direction) {
    if (item.dataset.applyRotation === 'true') {
      item.classList.remove('bottom-right', 'top-right', 'top-left', 'bottom-left')

      const rotationItem = item.querySelector('.valentine__item__image')
      if (direction.x === 1 && direction.y === 1) {
        rotationItem.style.transform = 'scaleX(1) rotate(45deg)'
        item.classList.add('bottom-right')
      } else if (direction.x === 1 && direction.y === -1) {
        rotationItem.style.transform = 'scaleX(1) rotate(-45deg)'
        item.classList.add('top-right')
      } else if (direction.x === -1 && direction.y === -1) {
        rotationItem.style.transform = 'scaleX(-1) rotate(-45deg)'
        item.classList.add('top-left')
      } else if (direction.x === -1 && direction.y === 1) {
        rotationItem.style.transform = 'scaleX(-1) rotate(45deg)'
        item.classList.add('bottom-left')
      }
    } else {
      item.style.transform = 'scaleX(1)'
      if (direction.x === -1) {
        item.style.transform = 'scaleX(-1)'
      }
    }
  }

  static initValentineItem() {
    const valentineItem = document.createElement('div')
    valentineItem.classList.add('valentine__item')
    valentineItem.dataset.applyRotation = 'true'

    const valentineItemImage = document.createElement('img')
    valentineItemImage.src = '/images/valentine/love-arrow@2x.png'
    valentineItemImage.classList.add('valentine__item__image')

    valentineItem.addEventListener('click', function pickupArrow() {
      valentineItem.classList.add('arrow-picked')

      const quiverElement = document.querySelector('.cupids-quiver')
      const cupidsArrowsElement = quiverElement.querySelector('.cupids-quiver-arrows')
      const message = quiverElement.querySelector('.cupids-quiver-message')
      cupidsArrowsElement.innerHTML = parseInt(cupidsArrowsElement.innerHTML) + 1

      if (parseInt(cupidsArrowsElement.innerHTML) === 1) {
        message.innerHTML = 'cupid arrow collected!'
      } else {
        message.innerHTML = 'cupid arrows collected!'
      }

      quiverElement.classList.add('show')
      setTimeout(() => quiverElement.classList.remove('show'), 2500)

      valentineItem.removeEventListener('click', pickupArrow)
    })

    valentineItem.appendChild(valentineItemImage)
    document.querySelector('#content').prepend(valentineItem)

    return valentineItem
  }

  static draw(item, direction, speed, progress) {
    const newPositionX = item.getBoundingClientRect().x + progress * speed.x * direction.x
    const newPositionY = item.getBoundingClientRect().y + window.scrollY + progress * speed.y * direction.y
    Valentine.setItemPosition(item, newPositionX, newPositionY)
  }

  /*
   * EASINGS
   * */
  static linear(timeFraction) {
    return timeFraction
  }

  static quad(timeFraction) {
    return timeFraction ** 2
  }

  static circ(timeFraction) {
    return 1 - Math.sin(Math.acos(timeFraction))
  }

  static setItemPosition(item, x, y) {
    item.style.left = x + 'px'
    item.style.top = y + 'px'
  }

  static getRandomStartingPointOutsideBoundaries(itemBoundaries, screenBoundaries) {
    let x = 0
    let y = 0

    // top/bottom (true) or left/right (false)
    if (Valentine.randomIntFromInterval(0, 1)) {
      // set x position randomly
      x = Valentine.randomIntFromInterval(screenBoundaries.left, screenBoundaries.right)

      // spawn on top (true) or bottom (false)
      if (Valentine.randomIntFromInterval(0, 1)) {
        // set the y position to top
        y = screenBoundaries.top - itemBoundaries.height
      } else {
        // set the y position to bottom
        y = screenBoundaries.bottom
      }
    } else {
      // set y position randomly
      y = Valentine.randomIntFromInterval(screenBoundaries.top, screenBoundaries.bottom)

      // spawn on left (true) or right (false)
      if (Valentine.randomIntFromInterval(0, 1)) {
        // set the x position to left
        x = screenBoundaries.left - itemBoundaries.width
      } else {
        // set the x position to right
        x = screenBoundaries.right
      }
    }

    return { x, y }
  }

  static getDirection(item, screenBoundaries) {
    // - we split the screen in four parts
    // - to add a bit more of randomness to the direction

    const screenSafeMarginLeft = screenBoundaries.right / 4
    const screenSafeMarginRight = screenBoundaries.right - screenSafeMarginLeft
    let x = 1
    if (item.getBoundingClientRect().x >= screenSafeMarginRight) {
      x = -1
    } else if (item.getBoundingClientRect().x > screenSafeMarginLeft) {
      x = this.randomIntFromInterval(0, 1) ? 1 : -1
    }

    const screenSafeMarginTop = (screenBoundaries.bottom - screenBoundaries.top) / 4 + window.scrollY
    const screenSafeMarginBottom = screenBoundaries.bottom - screenBoundaries.top - screenSafeMarginTop + window.scrollY

    let y = 1
    if (item.getBoundingClientRect().y >= screenSafeMarginBottom) {
      y = -1
    } else if (item.getBoundingClientRect().y > screenSafeMarginTop) {
      y = this.randomIntFromInterval(0, 1) ? 1 : -1
    }

    return { x, y }
  }

  static randomIntFromInterval(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
}
